import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IContractDTO } from '@common-frontend-submodule/interfaces/contract/icontract.interface';
import { IContractCreationDTO } from '@common-frontend-submodule/interfaces/contract/icontract-creation.interface';
import { IContractUpdateDTO } from '@common-frontend-submodule/interfaces/contract/icontract-update.interface';
import { IGetContractsRequestDTO } from '@common-frontend-submodule/interfaces/contract/iget-contracts-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ContractRoutes } from '@common-frontend-submodule/enums/routes/contract-routes.enum';
import axios from 'axios';
import config from '../../boot/config';
import { Utils } from '../../lib/utils';
import { IErrorDTO } from '@common-frontend-submodule/interfaces/common/ierror.interface';

const apiClient = ApiClient.getInstance()

interface ContractContextProps {
  getById: (contractId: string) => Promise<IApiResponseGenericDTO<IContractDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IContractDTO[]>>;
  filter: (model: IGetContractsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IContractDTO>>>;
  create: (model: IContractCreationDTO) => Promise<IApiResponseGenericDTO<IContractDTO>>;
  update: (contractId: string, model: IContractUpdateDTO) => Promise<IApiResponseGenericDTO<IContractDTO>>;
  delete: (contractId: string) => Promise<IApiResponseGenericDTO<boolean>>;
  downloadTemplate: () => Promise<void>;
  getAllClientContracts: (clientId: string) => Promise<IApiResponseGenericDTO<IContractDTO[]>>;
}

const ContractContext = createContext<ContractContextProps | undefined>(undefined);

export const ContractProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (contractId: string): Promise<IApiResponseGenericDTO<IContractDTO>> => {
    return await apiClient.getRequest<IContractDTO>(`${ContractRoutes.BasePrefix}/${contractId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IContractDTO[]>> => {
    return await apiClient.getRequest<IContractDTO[]>(`${ContractRoutes.BasePrefix}/${ContractRoutes.AllByClient}`);
  };

  const filter = async (model: IGetContractsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IContractDTO>>> => {
    return await apiClient.postRequest<IGetContractsRequestDTO, IPaginatedResultDTO<IContractDTO>>(
      `${ContractRoutes.BasePrefix}/${ContractRoutes.List}`,
      model
    );
  };

  const create = async (model: IContractCreationDTO): Promise<IApiResponseGenericDTO<IContractDTO>> => {
    return await apiClient.postRequest<IContractCreationDTO, IContractDTO>(ContractRoutes.BasePrefix, model);
  };

  const update = async (contractId: string, model: IContractUpdateDTO): Promise<IApiResponseGenericDTO<IContractDTO>> => {
    return await apiClient.putRequest<IContractUpdateDTO, IContractDTO>(`${ContractRoutes.BasePrefix}/${contractId}`, model);
  };

  const deleteContract = async (contractId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${ContractRoutes.BasePrefix}/${contractId}`);
  };

  const downloadTemplate = async (): Promise<void> => {
    try {
      const response = await axios.get(`${config.main.public_url}/1.docx`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Агентский договор.docx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      return Utils.handleErrorResponse(error as IErrorDTO);
    }
  };

  const getAllClientContracts = async (clientId: string): Promise<IApiResponseGenericDTO<IContractDTO[]>> => {
    return await apiClient.getRequest<IContractDTO[]>(`${ContractRoutes.BasePrefix}/all/${clientId}`);
  };

  return (
    <ContractContext.Provider value={{ getAllClientContracts, getById, getAll, filter, create, update, delete: deleteContract, downloadTemplate }}>
      {children}
    </ContractContext.Provider>
  );
};

export const useContractStore = () => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error('useContractStore must be used within a ContractProvider');
  }
  return context;
};
